import { changeDocumentAgent, LEASE_STATUS_DRAFT, LEASE_STATUS_SENT_SIGNING } from '@app/actions/dashboard';
import { setCurrentDocument, refreshDocumentList, removeDocumentInList } from '@app/actions/document';
import { ToastTypes } from '@app/common/components/Toast';
import { EXCHANGE_OF_CONTRACTS } from '@app/config';
import { EXCHANGE_OF_CONTRACTS_API_PATH, UploadStatus } from '@app/constants/constants';
import {
    AuthorityToExchangeType,
    EocDocumentCategory,
    ExchangeOfContracts,
    Integration
} from '@app/types/ExchangeOfContracts';
import { getErrorMessageForToast } from '@app/utils/errorUtils';
import { getErrorObjectFromYupError, formatDocumentsForApi } from '@app/utils/formUtils';
import { useMutation } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import {
    ApiModel,
    FormModel as DraftFormModel,
    projectApiModel,
    formValuesSchema,
    projectFormErrors,
    doRequiredDocumentsHaveSignatures
} from '../ExchangeOfContracts.model';
import { useDispatch } from 'react-redux';
import useDoubleSendGuard from '@app/hooks/useDoubleSendGuard';
import useToast from '@app/hooks/useToast';
import useResendCounter from '@app/hooks/useResendCounter';
import { useHistory } from 'react-router';
import { FormModel as AwaitingCompletionFormModel } from '../eoc-summary/awaiting-completion-email-form/AwaitingCompletionEmailForm.model';
import { scrollToError } from '@app/utils/finalFormUtils';
import {
    doAllClientsHaveSignatures,
    getDocumentsWithUpdatedAnnotations,
    syncCustomPlaceholders
} from '@app/containers/PdfViewer/utils';
import { UploadedDocumentState } from '@app/containers/PdfViewer/components/useUploadedDocumentState';
import { getIntegrationLabel } from '@app/utils/labelUtils';
import { useState } from 'react';
import { LocalDocument, UploadedDocument, UploadedFile } from '@app/types/UploadADoc';
import { FileForUpload } from '@app/containers/shared/DocumentDrop';

export const useApi = (eocDocument: ExchangeOfContracts) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { addNewToast } = useToast();
    const doubleSendGuard = useDoubleSendGuard();
    const { startResendCounter } = useResendCounter();
    const [uploadDocumentType, setUploadDocumentType] = useState<string | undefined>(undefined);
    const [uploadProgressPercentage, setUploadProgressPercentage] = useState<number | undefined>(undefined);
    const [isUploadInProgress, setIsUploadInProgress] = useState<boolean>(false);

    const documentId = eocDocument.id;

    const errorToast = (text: string) => addNewToast(getErrorMessageForToast(text), ToastTypes.ERROR, true);
    const successToast = (text: string) => addNewToast(text, ToastTypes.SUCCESS, true);
    const warningToast = (text: string) => addNewToast(text, ToastTypes.WARNING, true);

    const uploadFilesMutation = useMutation<
        { documents: UploadedFile[] },
        AxiosError,
        { files: FileForUpload[]; onProgress: (progress: number) => void }
    >(
        async ({ files, onProgress }) => {
            const documentsForUpload = formatDocumentsForApi([...files]);

            const response = await axios.post(`api/document/documents-custom/${documentId}`, documentsForUpload, {
                onUploadProgress: (progressEvent: ProgressEvent) => {
                    const { loaded, total } = progressEvent;
                    const progressPercentage = Math.floor((loaded * 100) / total);
                    onProgress(progressPercentage);
                }
            });

            return response.data;
        },
        {
            onError: error => {
                window.rollbar.error('Failed to upload documents', error);
                errorToast('Failed to upload documents.');
                setUploadProgressPercentage(undefined);
                setUploadDocumentType(undefined);
            }
        }
    );

    const uploadFiles = async (files: FileForUpload[]): Promise<UploadedFile[] | undefined> => {
        try {
            setUploadProgressPercentage(0);
            const result = await uploadFilesMutation.mutateAsync({
                files,
                onProgress: (progress: number) => {
                    setUploadProgressPercentage(progress);
                }
            });
            setUploadProgressPercentage(undefined);

            return result.documents;
        } catch (error) {
            return undefined;
        }
    };

    const uploadFilesAndSave = async (
        files: FileForUpload[],
        values: DraftFormModel,
        uploadedDocuments: (UploadedDocument | LocalDocument)[],
        uploadedDocumentStateItems: UploadedDocumentState[],
        documentType?: string
    ): Promise<void> => {
        try {
            setUploadDocumentType(documentType);
            setUploadProgressPercentage(0);
            setIsUploadInProgress(true);

            const result = await uploadFilesMutation.mutateAsync({
                files,
                onProgress: (progress: number) => {
                    setUploadProgressPercentage(progress);
                }
            });
            const newDocuments = result.documents.map((document: UploadedFile) => ({
                document,
                documentType: documentType
            }));

            let newUploadedDocuments = [];
            if (documentType === EocDocumentCategory.SALES_CONTRACT) {
                // If the document type is SALES_CONTRACT, we want to add the new documents at the beginning
                newUploadedDocuments = [...newDocuments, ...uploadedDocuments];
            } else {
                newUploadedDocuments = [...uploadedDocuments, ...newDocuments];
            }

            await save(values, newUploadedDocuments, uploadedDocumentStateItems, { silent: true });
        } finally {
            setIsUploadInProgress(false);
            setUploadProgressPercentage(undefined);
            setUploadDocumentType(undefined);
        }
    };

    type SaveResponse = {
        doc: ExchangeOfContracts;
        displayAutoPositioningFailMessage?: boolean;
    };

    const saveMutation = useMutation<SaveResponse, AxiosError, ApiModel>(
        async values => {
            const response = await axios.post(`${EXCHANGE_OF_CONTRACTS_API_PATH}/${documentId}`, values);
            return response.data;
        },
        {
            onError: error => {
                window.rollbar.error('Failed to save exchange of contracts', error);
                errorToast('Failed to save the document.');
            }
        }
    );

    const sendMutation = useMutation<ExchangeOfContracts, AxiosError, ApiModel>(
        async values => {
            const response = await axios.post(`${EXCHANGE_OF_CONTRACTS_API_PATH}/${documentId}/send`, values);
            return response.data.doc;
        },
        {
            onError: error => {
                if (error.response?.status !== 400 || error?.response?.data?.originError?.isAnnotationError) {
                    window.rollbar.error('Failed to send exchange of contracts', error);
                    errorToast('Failed to send the document.');
                }
            }
        }
    );

    const updateAgentMutation = useMutation<void, AxiosError, string>(
        async agentId => {
            await axios.put(`/api/document/${eocDocument.id}/agent`, { agent: agentId });
        },
        {
            onError: error => {
                window.rollbar.error('Failed to update the agent', error);
                errorToast('Failed to update the agent.');
            }
        }
    );

    const unlinkIntegrationMutation = useMutation(
        async () => {
            const response = await axios.delete(`/api/document/${eocDocument.id}/integration`);
            return response.data.doc;
        },
        {
            onError: error => {
                window.rollbar.error('Failed to unlink integration', error);
                errorToast('Something went wrong, please try again.');
            }
        }
    );

    async function unlinkIntegration(doc: ExchangeOfContracts) {
        const document = await unlinkIntegrationMutation.mutateAsync();
        dispatch(setCurrentDocument(document));
        dispatch(refreshDocumentList());
        successToast(
            `${getIntegrationLabel(doc.integration?.type)} integration for ${doc.integration?.address} has been successfully unlinked.`
        );
    }

    const linkWithIntegrationMutation = useMutation(
        async (values: Integration) => {
            const response = await axios.post(`/api/document/${eocDocument.id}/integration`, values);
            return response.data.doc;
        },
        {
            onError: error => {
                window.rollbar.error('Failed to link integration', error);
                errorToast('Something went wrong, please try again.');
            }
        }
    );

    async function linkWithIntegration(values: Integration) {
        const document = await linkWithIntegrationMutation.mutateAsync(values);
        dispatch(setCurrentDocument(document));
        dispatch(refreshDocumentList());
        successToast(
            `${getIntegrationLabel(document.integration.type)} integration for ${document.integration.address} has been successfully linked.`
        );
    }

    const save = async (
        values: DraftFormModel,
        uploadedDocuments: ApiModel['uploadedDocuments'],
        uploadedDocumentStateItems: UploadedDocumentState[],
        options?: { silent?: boolean }
    ) => {
        try {
            syncCustomPlaceholders(uploadedDocuments, uploadedDocumentStateItems, values.customPlaceholders);
            const documentsWithUpdatedAnnotations = await getDocumentsWithUpdatedAnnotations(
                uploadedDocuments,
                uploadedDocumentStateItems
            );

            const { doc } = await saveMutation.mutateAsync(projectApiModel(values, documentsWithUpdatedAnnotations));
            dispatch(setCurrentDocument(doc));
            dispatch(refreshDocumentList());
            if (!options?.silent) {
                successToast('Document saved successfully');
            }
        } catch (error) {
            return undefined;
        }
    };

    const validate = (
        values: DraftFormModel,
        uploadedDocuments: ApiModel['uploadedDocuments'],
        uploadedDocumentStateItems: UploadedDocumentState[]
    ) => {
        try {
            const recipients = [...values.purchasers, ...values.vendors];
            const doAllRecipientsHaveSignatures = doRequiredDocumentsHaveSignatures(
                recipients.length,
                uploadedDocuments,
                uploadedDocumentStateItems
            );
            formValuesSchema.validateSync(
                { ...values, uploadedDocuments, doAllRecipientsHaveSignatures },
                { abortEarly: false }
            );
        } catch (error) {
            const errors = getErrorObjectFromYupError(error);
            return errors;
        }
    };

    const send = async (
        values: DraftFormModel,
        uploadedDocuments: ApiModel['uploadedDocuments'],
        uploadedDocumentStateItems: UploadedDocumentState[]
    ) => {
        return doubleSendGuard(async () => {
            try {
                syncCustomPlaceholders(uploadedDocuments, uploadedDocumentStateItems, values.customPlaceholders);
                const documentsWithUpdatedAnnotations = await getDocumentsWithUpdatedAnnotations(
                    uploadedDocuments,
                    uploadedDocumentStateItems
                );

                // Frontend validation
                const errors = validate(values, documentsWithUpdatedAnnotations, uploadedDocumentStateItems);
                if (errors) {
                    return errors;
                }

                const document = await sendMutation.mutateAsync(
                    projectApiModel(values, documentsWithUpdatedAnnotations)
                );
                dispatch(removeDocumentInList(document, LEASE_STATUS_DRAFT));
                dispatch(setCurrentDocument(document));
                history.push(`/dashboard/documents/${EXCHANGE_OF_CONTRACTS}/${LEASE_STATUS_SENT_SIGNING}`);
                successToast('Document sent successfully');
                startResendCounter();
                return undefined;
            } catch (error) {
                // Backend validation
                const apiErrors = error.response?.data?.errors;
                if (apiErrors) {
                    return projectFormErrors(apiErrors, values);
                }
            }
        });
    };

    const updateAgent = async (agentId: string) => {
        await updateAgentMutation.mutateAsync(agentId);
        dispatch(changeDocumentAgent());
    };

    const resendMutation = useMutation(
        (data: { selectedClients: string[] }) => {
            return axios.post(`api/document/exchange-of-contracts/${eocDocument.id}/resend`, {
                selectedClients: data.selectedClients
            });
        },
        {
            onError: error => {
                window.rollbar.error('Failed to resend exchange of contracts', error);
                errorToast('Failed to send the document.');
            }
        }
    );

    const resend = async (selectedClients: string[], onSuccess: () => void) => {
        await resendMutation.mutateAsync({ selectedClients });
        successToast('Document sent successfully');
        dispatch(refreshDocumentList());
        onSuccess();
    };

    const completeMutation = useMutation<void, AxiosError, AwaitingCompletionFormModel>(
        data => {
            return axios.post(`api/document/exchange-of-contracts/${eocDocument.id}/complete`, data);
        },
        {
            onError: error => {
                window.rollbar.error('Failed to complete exchange of contracts', error);
                errorToast('Failed to complete the document.');
            }
        }
    );

    const complete = async (values: AwaitingCompletionFormModel) => {
        await completeMutation.mutateAsync(values);
        successToast('Document completed successfully');
        dispatch(refreshDocumentList());
    };

    const addAuthorityToExchangeMutation = useMutation(
        async (templateType: AuthorityToExchangeType) => {
            const response = await axios.post(
                `api/document/exchange-of-contracts/${eocDocument.id}/authority-to-exchange`,
                {
                    template: templateType
                }
            );
            return response.data.doc;
        },
        {
            onError: error => {
                window.rollbar.error('Failed to add authority to exchange', error);
                errorToast('Failed to add authority to exchange.');
            }
        }
    );

    const addAuthorityToExchange = async (templateType: AuthorityToExchangeType) => {
        const document = await addAuthorityToExchangeMutation.mutateAsync(templateType);
        dispatch(setCurrentDocument(document));
        refreshDocumentList();
    };

    const uploadDocumentsToIntegrationMutation = useMutation(() => {
        return axios.post(`/api/document/exchange-of-contracts/${eocDocument.id}/upload-documents-to-integration`);
    });

    const uploadDocumentsToIntegration = async () => {
        await uploadDocumentsToIntegrationMutation.mutateAsync();
    };

    function getIntegrationUploadStatus(): UploadStatus {
        if (uploadDocumentsToIntegrationMutation.isLoading) {
            return UploadStatus.UPLOADING;
        } else if (uploadDocumentsToIntegrationMutation.isError) {
            return UploadStatus.FAILED;
        } else if (uploadDocumentsToIntegrationMutation.isSuccess) {
            return UploadStatus.SUCCESS;
        }
        return eocDocument.integration?.uploadStatus || UploadStatus.DEFAULT;
    }

    const integrationUploadStatus = getIntegrationUploadStatus();

    return {
        scrollToError,
        save,
        send,
        updateAgent,
        resend,
        complete,
        uploadFilesAndSave,
        uploadFiles,
        uploadProgress:
            uploadDocumentType !== EocDocumentCategory.SALES_CONTRACT ? uploadProgressPercentage : undefined,
        contractUploadProgress:
            uploadDocumentType === EocDocumentCategory.SALES_CONTRACT ? uploadProgressPercentage : undefined,
        isUploading: uploadFilesMutation.isLoading,
        unlinkIntegration,
        linkWithIntegration,
        uploadDocumentsToIntegration,
        addAuthorityToExchange,
        isApiBusy:
            saveMutation.isLoading ||
            sendMutation.isLoading ||
            updateAgentMutation.isLoading ||
            resendMutation.isLoading ||
            completeMutation.isLoading ||
            uploadFilesMutation.isLoading ||
            unlinkIntegrationMutation.isLoading ||
            linkWithIntegrationMutation.isLoading ||
            addAuthorityToExchangeMutation.isLoading ||
            uploadDocumentsToIntegrationMutation.isLoading ||
            isUploadInProgress,
        isSaving: saveMutation.isLoading,
        isSending: sendMutation.isLoading,
        isResending: resendMutation.isLoading,
        isCompleting: completeMutation.isLoading,
        isUpdatingAgent: updateAgentMutation.isLoading,
        isUnlinkingIntegration: unlinkIntegrationMutation.isLoading,
        isLinkingIntegration: linkWithIntegrationMutation.isLoading,
        isAddingAuthorityToExchange: addAuthorityToExchangeMutation.isLoading,
        integrationUploadStatus
    };
};
